import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import type { DirectedHistoryEntry } from 'fym-shared/src/music24/directedVideoApi';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../music24/components/primitives/SMLButton';
import { formatTime } from '../../musicVideoEditor/timeline/timelineGeometry';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { howMadeMessages as messages } from '../messages/howMadeMessages';
import { sectionAt, sectionDisplayName, sectionToneOf } from '../shell/sectionBar';
import { DIRECTED_STEP_NAMES } from '../shell/DirectedStepper';
import { SECTION_DOT } from '../steps/director/sectionDots';
import { clipNumbers, clipStillsOf } from '../steps/script/scriptEdits';
import { useDirectedCastNames } from '../useDirectedCastNames';
import { scriptChanges } from './howMadeRules';
import { HowMadeSection } from './HowMadeSection';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for "How it was made"; each part uses its own slice of it */
import styles from './HowMade.module.scss';
import steps from '../steps/DirectorSteps.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The shots that show before "Show all" (design l1). */
const FIRST_SHOTS = 6;

/**
 * Step 4 of "How it was made" (design l1): the script as it went to the make, one row per shot
 * (time, section, the still of its clip, the scene and the camera, the clip), and what changed.
 */
const HowMadeScriptComponent: React.FC<{ entries: readonly DirectedHistoryEntry[] }> = ({ entries }) => {
  const intl = useIntl();
  const { video, song } = useDirectedVideoContext();
  const { named } = useDirectedCastNames();
  const [isAll, setIsAll] = useState(false);
  const { script } = video;
  const numbers = useMemo(() => (script === null ? new Map<string, number>() : clipNumbers(script)), [script]);
  const stills = useMemo(() => clipStillsOf(video.clipEntries), [video.clipEntries]);
  const changes = scriptChanges(entries);
  const shots = script?.shots ?? [];
  const shown = isAll ? shots : shots.slice(0, FIRST_SHOTS);

  return (
    <HowMadeSection
      step="script"
      eyebrow={intl.formatMessage(messages.stepEyebrow, {
        number: 4,
        name: intl.formatMessage(DIRECTED_STEP_NAMES.script),
      })}
      title={
        script === null
          ? intl.formatMessage(messages.scriptNone)
          : intl.formatMessage(messages.scriptTitle, {
              shots: shots.length,
              clips: numbers.size,
              effects: script.effects.length,
            })
      }
      text={script === null ? undefined : intl.formatMessage(messages.scriptText, changes)}
    >
      {shots.length > 0 && (
        <div className={styles.shots}>
          {shown.map((shot, index) => {
            const section = sectionAt(song.sections, shot.startSec);
            const still = stills.get(shot.clipKey) ?? null;
            const number = numbers.get(shot.clipKey) ?? 0;
            return (
              <div key={shot.id} className={styles.shot} data-testid={`made-shot-${shot.id}`}>
                <span className={styles.shotNumber}>{index + 1}</span>
                <div className={styles.shotTime}>
                  <span>{`${formatTime(shot.startSec)}–${formatTime(shot.endSec)}`}</span>
                  {section !== null && (
                    <span className={styles.shotSection}>
                      <i className={clsx(steps.dot, SECTION_DOT[sectionToneOf(section.kind)])} aria-hidden />
                      {sectionDisplayName(section.label)}
                    </span>
                  )}
                </div>
                <div className={styles.shotThumb}>{still !== null && <img src={still} alt="" loading="lazy" />}</div>
                <div className={styles.shotText}>
                  <span className={styles.shotScene}>{named(shot.scene)}</span>
                  {shot.camera.trim() !== '' && <span className={styles.shotCamera}>{shot.camera}</span>}
                </div>
                <span className={styles.shotClip}>
                  {shot.clipInSec > 0
                    ? intl.formatMessage(messages.clipIn, { number, sec: shot.clipInSec.toFixed(1) })
                    : intl.formatMessage(messages.clip, { number })}
                </span>
              </div>
            );
          })}
          {shots.length > FIRST_SHOTS && (
            <div className={styles.shotsMore}>
              <SMLButton onClick={() => setIsAll((value) => !value)}>
                {isAll
                  ? intl.formatMessage(messages.showFewer)
                  : intl.formatMessage(messages.showAll, { count: shots.length })}
              </SMLButton>
            </div>
          )}
        </div>
      )}
    </HowMadeSection>
  );
};

export const HowMadeScript = TypedMemo(HowMadeScriptComponent);
